import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import _isEmpty from 'lodash/isEmpty';

import Header from '../../components/Header';
import LocationCheck from '../../components/LocationCheck/LocationCheck';
import PendingKycBanner from '../Kyc/PendingKycBanner';
import NameMismatchSheet from './components/NameMismatchSheet';
import ScreenNudgeSheet from './components/ScreenNudgeSheet';
import { useUserStore } from '../../storage/userState';
import { useTokenStore } from '../../storage/tokenState';
import { NAVIGATION_ROUTES, CLEVERTAP_EVENTS } from '../../constant';
import { pushClevertapEvent } from '../../utils/analytics/clevertap';
import { fetchAndStoreUserDetails } from '../../services/AuthService';
import type { ELanguage } from '../../storage/store';
import { formatRupees } from '../Withdraw/helper';
import {
  getWithdrawEligibility,
  resolveInAppCta,
  MASKED_BALANCE,
} from './helper';
import { isKycApproved } from '../Kyc/helper';
import { useCompletePendingKyc } from '../Kyc/useCompletePendingKyc';
import { fetchWithdrawalGate } from '../../api/useWithdrawalGate';
import { showLocationModal } from '../../store/actions/actions';
import { setShownNudgeId } from '../../storage/withdrawState';
import type {
  INameMismatchBottomSheet,
  IScreenNudge,
} from '../../interfaces/Withdrawal';
import styles from './styles.module.css';

/**
 * Wallet — the entry point into withdrawal.
 *
 * Deliberately minimal: winnings balance and a Withdraw CTA. No gems, no
 * transaction history.
 *
 * Viewable logged out (showing ₹0), where the CTA routes to sign-in and asks to
 * be sent back here afterwards. Sign-in cannot decide eligibility itself —
 * `OtpScreen` navigates before user details have loaded — so this screen
 * forwards to `/withdraw` once the balance arrives.
 */
const Wallet: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { userDetails } = useUserStore();
  const { token: userToken, language } = useTokenStore();
  const isLoggedIn = !_isEmpty(userToken);

  const { balance, minWithdrawal, isEligible } =
    getWithdrawEligibility(userDetails);

  // Captured once on mount, then wiped from history immediately: leaving the
  // flag in place would re-trigger the forward every time the user navigates
  // back here from /withdraw, trapping them in a loop.
  const autoWithdrawRef = useRef<boolean>(
    Boolean((location.state as { autoWithdraw?: boolean })?.autoWithdraw),
  );
  const hasForwardedRef = useRef(false);

  const [isChecking, setIsChecking] = useState(false);
  const [mismatch, setMismatch] = useState<INameMismatchBottomSheet | undefined>();
  const [nudge, setNudge] = useState<IScreenNudge | undefined>();

  useEffect(() => {
    if (!(location.state as { autoWithdraw?: boolean })?.autoWithdraw) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WALLET_SCREEN);
  }, []);

  // User details are otherwise fetched only at login, so the balance would be
  // whatever it was when the session started. Refresh on entry; a failure just
  // leaves the stored values in place rather than erroring the screen.
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchAndStoreUserDetails(language as ELanguage);
  }, [isLoggedIn, language]);

  /**
   * Android gates entry to withdrawal on getWithdrawalScreenBottomsheet and
   * branches before navigating. Each branch is gated on its field being
   * present, so an unexpected shape falls through to PROCEED rather than
   * blocking withdrawal.
   *
   * Every route from this screen into /withdraw goes through here — both the
   * button and the post-sign-in auto-forward — so the gate cannot be skipped.
   */
  const runWithdrawalGate = useCallback(async () => {
    // Checked before the server gate: an unverified user cannot withdraw at
    // all, so sending them to KYC is more useful than any sheet the gate
    // would return.
    // Read live rather than from the closure: when this runs straight after a
    // KYC completion, the captured `userDetails` is still the pre-approval value
    // and would bounce the user back to /kyc in a loop.
    const latestDetails = useUserStore.getState().userDetails;

    if (!isKycApproved(latestDetails)) {
      // Carries the withdrawal intent across the KYC round trip, so an approved
      // user comes back into the gate instead of having to start over.
      navigate(NAVIGATION_ROUTES.KYC, {
        state: {
          returnTo: NAVIGATION_ROUTES.WALLET,
          resumeWithdraw: true,
        },
      });
      return;
    }

    setIsChecking(true);
    try {
      const gate = await fetchWithdrawalGate();

      switch (gate.kind) {
        case 'LOCATION':
          // Reuses the same sheet TournamentDetails shows, driven by redux.
          dispatch(showLocationModal(gate.location));
          return;
        case 'NUDGE':
          setShownNudgeId(gate.nudge?._id);
          setNudge(gate.nudge);
          return;
        case 'MISMATCH':
          setMismatch(gate.mismatch);
          return;
        case 'ERROR':
          // Deliberately does not navigate: this is the only compliance check
          // before a money transfer, so failing open is not acceptable.
          toast.error(gate.error || t('errorOccurred'));
          return;
        default:
          navigate(NAVIGATION_ROUTES.WITHDRAW);
      }
    } finally {
      setIsChecking(false);
    }
  }, [dispatch, navigate, t]);

  // The DigiLocker redirect target is set on the backend and is not guaranteed
  // to be /kyc; anything unmatched lands here via the catch-all route. Completing
  // from Wallet too means a stray return path still finishes the verification
  // instead of silently stranding it.
  const {
    hasPending: hasPendingKyc,
    isCompleting: isCompletingKyc,
    retry: retryKyc,
  } = useCompletePendingKyc({ onApproved: () => runWithdrawalGate() });

  // Post sign-in hand-off: forward exactly once, and only when the balance has
  // actually loaded — `userDetails` is empty for a moment after login.
  useEffect(() => {
    if (!autoWithdrawRef.current || hasForwardedRef.current) return;
    if (!isLoggedIn || _isEmpty(userDetails)) return;

    autoWithdrawRef.current = false;
    if (!isEligible) return;

    hasForwardedRef.current = true;
    // Through the gate, not straight to /withdraw: signing in from the Withdraw
    // button used to land here and forward past every check, so a blocked user
    // reached the withdrawal screen without ever seeing why.
    runWithdrawalGate();
  }, [isLoggedIn, userDetails, isEligible, runWithdrawalGate]);

  const handleWithdraw = () => {
    if (isChecking) return;

    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WALLET_WITHDRAW_CLICKED, {
      isLoggedIn,
      balance,
    });

    if (!isLoggedIn) {
      // SignIn forwards this state straight through to OtpScreen, which routes
      // by `returnTo` on success.
      navigate(NAVIGATION_ROUTES.SIGNIN, {
        state: {
          returnTo: NAVIGATION_ROUTES.WALLET,
          autoWithdraw: true,
        },
      });
      return;
    }

    runWithdrawalGate();
  };

  const handleGateCta = (route: string | null) => {
    setMismatch(undefined);
    setNudge(undefined);
    if (route) navigate(route);
  };

  // Logged out, the CTA is a route to sign-in, so it must stay tappable.
  const isCtaDisabled = isLoggedIn && !isEligible;

  return (
    <div className={styles.walletPage}>
      <Header pageTitle={t('walletTitle')} />

      <div className={styles.walletBody}>
        <div className={styles.balanceCard}>
          <p className={clsx(styles.balanceLabel, 'fontSizeSm14 fontColor666')}>
            {t('winningsBalanceLabel')}
          </p>
          <p className={clsx(styles.balanceValue, 'fontWeightBold')}>
            {isLoggedIn ? formatRupees(balance) : MASKED_BALANCE}
          </p>
        </div>

        {isCtaDisabled && (
          <p className={clsx(styles.helperText, 'fontSizeSm12 fontColor666')}>
            {minWithdrawal
              ? t('minWithdrawToStart', { min: formatRupees(minWithdrawal) })
              : t('noWinningsToWithdraw')}
          </p>
        )}
      </div>

      <div className={styles.ctaBar}>
        <button
          type="button"
          className="btn primaryBtn"
          disabled={isCtaDisabled || isChecking}
          onClick={handleWithdraw}
        >
          {isChecking ? t('pleaseWait') : t('withdrawLabel')}
        </button>
      </div>

      <NameMismatchSheet
        isOpen={Boolean(mismatch)}
        data={mismatch}
        onConfirm={() => handleGateCta(resolveInAppCta(mismatch?.posCta))}
        onClose={() => setMismatch(undefined)}
      />

      <ScreenNudgeSheet
        isOpen={Boolean(nudge)}
        nudge={nudge}
        onAgree={() => handleGateCta(resolveInAppCta(nudge?.agreeCta))}
        onDismiss={() => setNudge(undefined)}
      />

      <LocationCheck />

      {/* A verification that came back unfinished is retried from here rather
          than by sending the user somewhere else — completion already runs on
          this screen. */}
      <PendingKycBanner
        hasPending={hasPendingKyc}
        isCompleting={isCompletingKyc}
        onRetry={retryKyc}
      />
    </div>
  );
};

export default Wallet;
